import React, { useState, useCallback, useRef } from 'react';
import {
  useTheme,
  useMediaQuery,
  Box,
  Typography,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  alpha,
} from '@mui/material';
import {
  ManageSearch,
  Psychology,
  School,
  Analytics,
  Group,
  PersonSearch,
  AlignHorizontalLeft,
  ErrorOutline,
  CheckCircleOutline,
  Warning,
  Functions,
} from '@mui/icons-material';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import api from 'state/axios';
import useAuthCheck from 'hooks/useAuthCheck';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { LAYOUT_HEIGHT } from 'state/constants';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import MetricCard from 'components/MetricCard';
import ScrollToTop from 'components/ScrollToTop';
import AnomalyPairGrid from './components/AnomalyPairGrid';
import AnomalyEntityGrid from './components/AnomalyEntityGrid';
// import ModelStatusChip from './components/ModelStatusChip';

// ─── Train Dialog ─────────────────────────────────────────────────────────────

const TrainDialog = ({ open, onClose, startDate, endDate }) => {
  const [epochs,  setEpochs]  = useState(150);
  const [loading, setLoading] = useState(false);
  const [result,  setResult]  = useState(null);

  const handleTrain = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post('/anomaly/train', {
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate:   format(endDate,   'yyyy-MM-dd'),
        epochs,
      });
      setResult({ success: true, msg: data.message });
      toast.success('Autoencoder trained successfully');
    } catch (err) {
      setResult({ success: false, msg: err.response?.data?.message ?? err.message });
      toast.error('Training failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 16 }}>
        <Psychology fontSize="small" /> Train TF.js Autoencoder
      </DialogTitle>
      <DialogContent>
        <Stack gap={2} mt={1}>
          <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
            Training on <b>{format(startDate, 'dd MMM yyyy')}</b> – <b>{format(endDate, 'dd MMM yyyy')}</b>.
            The autoencoder learns normal behaviour and flags deviations.
            Requires ≥ 10 user-crew pairs.
          </Alert>
          <TextField
            label="Epochs"
            type="number"
            size="small"
            value={epochs}
            onChange={(e) => setEpochs(Math.max(20, Math.min(500, +e.target.value)))}
            inputProps={{ min: 20, max: 500, step: 10 }}
            helperText="150 recommended. Higher = more accurate but slower."
          />
          {result && (
            <Alert
              severity={result.success ? 'success' : 'error'}
              icon={result.success ? <CheckCircleOutline /> : <ErrorOutline />}
            >
              {result.msg}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          size="small"
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <School />}
          onClick={handleTrain}
          disabled={loading}
        >
          {loading ? 'Training…' : 'Start Training'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Stats cards data ─────────────────────────────────────────────────────────

const buildStats = (result, theme) => {
  if (!result) return [];
  const { summary = {}, anomalous = {} } = result;
  const criticalHigh = (anomalous.pairs ?? []).filter(
    (p) => p.risk === 'CRITICAL' || p.risk === 'HIGH'
  ).length;
  return [
    {
      title: 'Logs Analysed',
      subtitle: 'Total records',
      value: result.totalLogs?.toLocaleString('en-US') ?? 0,
      icon: <Functions />,
      bgColor: alpha(theme.palette.primary.main, 0.6),
    },
    {
      title: 'Total Combined',
      subtitle: 'User - Crew pairs',
      value: summary.pairs ?? 0,
      icon: <AlignHorizontalLeft />,
      bgColor: alpha(theme.palette.primary.dark, 0.5),
    },
    {
      title: 'Suspicious Pairs',
      subtitle: `Flagged as anomalous`,
      value: anomalous.pairs?.length ?? 0,
      icon: <Warning />,
      bgColor: alpha(theme.palette.error.main, 0.6),
    },
    {
      title: 'Critical / High',
      subtitle: 'Review immediately',
      value: criticalHigh,
      icon: <ErrorOutline />,
      bgColor: alpha(theme.palette.secondary.main, 0.6),
    },
    {
      title: 'Anomalous Users',
      subtitle: `of ${summary.users ?? 0} users`,
      value: anomalous.users?.length ?? 0,
      icon: <PersonSearch />,
      bgColor: alpha(theme.palette.warning.main, 0.6),
    },
    {
      title: 'Affected Crew',
      subtitle: `of ${summary.crews ?? 0} crew`,
      value: anomalous.crews?.length ?? 0,
      icon: <Group />,
      bgColor: alpha(theme.palette.info.main, 0.6),
    },
  ];
};

// ─── View toggle options ──────────────────────────────────────────────────────

const VIEWS = [
  { value: 'overview', label: 'Overview',        icon: <Analytics /> },
  { value: 'pairs',    label: 'Collusion Pairs', icon: <AlignHorizontalLeft /> },
  { value: 'users',    label: 'Users',           icon: <PersonSearch /> },
  { value: 'crews',    label: 'Crew',            icon: <Group /> },
];

// ─── Main Page ────────────────────────────────────────────────────────────────

const AnomalyDetection = () => {
  useAuthCheck();
  const theme     = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet  = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const { startDate, endDate } = useDateRangeContext();

  const scrollRef = useRef(null);
  const [loading,   setLoading]   = useState(false);
  const [result,    setResult]    = useState(null);
  const [error,     setError]     = useState(null);
  const [view,      setView]      = useState('overview');
  const [trainOpen, setTrainOpen] = useState(false);

  const detect = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post('/anomaly/detect', {
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate:   format(endDate,   'yyyy-MM-dd'),
      });
      setResult(data);
      const total =
        (data.anomalous?.pairs?.length ?? 0) +
        (data.anomalous?.users?.length ?? 0) +
        (data.anomalous?.crews?.length ?? 0);
      if (total === 0) toast('No anomalies detected', { icon: '✅' });
      else toast.error(`${total} anomalous entities detected`);
    } catch (err) {
      setError(err.response?.data?.message ?? err.message);
      toast.error('Detection failed');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  const stats      = buildStats(result, theme);
  const anomalous  = result?.anomalous ?? {};
  const allData    = result?.all       ?? {};
  // const modelStat  = result?.modelStatus;
  const hasCritical = (anomalous.pairs ?? []).some((p) => p.risk === 'CRITICAL');

  const gridSpan = isDesktop ? 'span 2' : isTablet ? 'span 4' : 'span 6';

  return (
    <Box className="main-container-box">
      {/* ── Header row ── */}
      <FlexBetween>
        <Header
          title="Anomaly Detection"
          subtitle={
            loading ? 'Analysing…'
            : result  ? `${result.totalLogs?.toLocaleString()} logs`
            : 'AI Power'
          }
        />

        <Box 
          display="flex" 
          alignItems="center" 
          flexDirection={isMobile ? "column" : "row"} 
          gap={1}
        >
          {/* <Box display="flex" alignItems="center" gap={1}>
            {modelStat && (
              <ModelStatusChip status={{ ...modelStat, method: result?.method }} />
            )}
          </Box> */}
          
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Train on selected date range" arrow>
              <span>
                <StyledButton onClick={() => setTrainOpen(true)} disabled={loading}>
                  <Psychology />
                </StyledButton>
              </span>
            </Tooltip>

            <Tooltip title="Run anomaly detection" arrow>
              <span>
                <StyledButton onClick={detect} disabled={loading}>
                  {loading
                    ? <CircularProgress size={18} color="inherit" />
                    : <ManageSearch />}
                </StyledButton>
              </span>
            </Tooltip>
            
            {!isMobile && (
              <ToggleButtonGroup
                aria-label="View"
                exclusive
                value={view}
                onChange={(_, v) => v && setView(v)}
              >
                {VIEWS.map(({ value, label, icon }) => (
                  <Tooltip key={value} title={label} arrow>
                    <ToggleButton 
                      aria-label={label}
                      size="small" 
                      value={value} 
                    >
                      {icon}
                    </ToggleButton>
                  </Tooltip>
                ))}
              </ToggleButtonGroup>
            )}            
          </Box>
        </Box>
      </FlexBetween>

      {/* ── Body ── */}
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">{error}</Typography>
        </FlexCenter>
      ) : !result ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Box textAlign="center">
            <ManageSearch sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
            <Typography variant="h5" color="text.secondary">
              Select a date range and click detect to run analysis
            </Typography>
          </Box>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gap={2}
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
        >
          <Box 
            ref={scrollRef} 
            height={LAYOUT_HEIGHT}
            display="flex" 
            flexDirection="column" 
            gap={2}
            sx={{ 
              gridColumn: "span 12", 
              overflowY: 'auto', 
              pr: !isMobile && view === 'overview' ? 0.5 : 0 
            }}
          >
            {/* Overview */}
            {view === 'overview' && (
              <Box display="flex" flexDirection="column" gap={2}>
                <Box
                  display="grid"
                  gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                  gridAutoRows={100}
                  gap={2}
                  sx={{ '& > div': { gridColumn: gridSpan } }}
                >
                  {stats.map((s, i) => (
                    <MetricCard
                      key={i}
                      title={s.title}
                      subtitle={s.subtitle}
                      value={s.value}
                      icon={s.icon}
                      bgColor={s.bgColor}
                    />
                  ))}
                </Box>
              
                {(anomalous.pairs?.length ?? 0) > 0 && (
                  <Box sx={{ width: '100%', minWidth: 0 }}>
                    <Typography
                      variant="h6"
                      mb={1}
                      color={hasCritical ? 'error.main' : 'text.primary'}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      {hasCritical && <ErrorOutline fontSize="small" />}
                      Suspicious Collusion Pairs
                      {hasCritical && (
                        <Box component="span" fontWeight="bold">
                          ({ anomalous.pairs.filter(p => p.risk === 'CRITICAL').length} Critical)
                        </Box>
                      )}
                    </Typography>
                    <Box
                      height={Math.min((anomalous.pairs.length + 1) * 36 + 56, 450)}
                      sx={{ overflowX: 'auto' }}
                    >
                      <AnomalyPairGrid rows={anomalous.pairs.slice(0, 20)} />
                    </Box>
                  </Box>
                )}

                {(anomalous.users?.length ?? 0) > 0 && (
                  <Box sx={{ width: '100%', minWidth: 0 }}>
                    <Typography variant="h6" mb={1}>
                      Anomalous Users
                    </Typography>
                    <Box
                      height={Math.min((anomalous.users.length + 1) * 36 + 56, 450)}
                      sx={{ overflowX: 'auto' }}
                    >
                      <AnomalyEntityGrid rows={anomalous.users.slice(0, 10)} type="user" />
                    </Box>
                  </Box>
                )}

                {(anomalous.crews?.length ?? 0) > 0 && (
                  <Box sx={{ width: '100%', minWidth: 0 }}>
                    <Typography variant="h6" mb={1}>
                      Affected Crew Members
                    </Typography>
                    <Box
                      height={Math.min((anomalous.crews.length + 1) * 36 + 56, 450)}
                      sx={{ overflowX: 'auto' }}
                    >
                      <AnomalyEntityGrid rows={anomalous.crews.slice(0, 10)} type="crew" />
                    </Box>
                  </Box>
                )}

                {!anomalous.pairs?.length && !anomalous.users?.length && !anomalous.crews?.length && (
                  <FlexCenter height="200px">
                    <Typography variant="h5" color="text.secondary">
                      No anomalies detected in the selected period
                    </Typography>
                  </FlexCenter>
                )}
              </Box>
            )}

            {/* Collusion Pairs - full list */}
            {view === 'pairs' && (
              <Box
                height={LAYOUT_HEIGHT}
                sx={{ width: '100%', minWidth: 0, overflowX: 'auto', overflowY: 'auto' }}
              >
                {(allData.pairs?.length ?? 0) === 0
                  ? (
                      <FlexCenter height="100%">
                        <Typography variant="h5" color="text.secondary">
                          No data available
                        </Typography>
                      </FlexCenter>
                  ) : (
                    <AnomalyPairGrid rows={allData.pairs} />
                  )
                }
              </Box>
            )}

            {/* Users - full list */}
            {view === 'users' && (
              <Box
                height={LAYOUT_HEIGHT}
                sx={{ width: '100%', minWidth: 0, overflowX: 'auto', overflowY: 'auto' }}
              >
                {(allData.users?.length ?? 0) === 0
                  ? (
                      <FlexCenter height="100%">
                        <Typography variant="h5" color="text.secondary">
                          No data available
                        </Typography>
                      </FlexCenter>
                  ) : (
                    <AnomalyEntityGrid rows={allData.users} type="user" />
                  )
                }
              </Box>
            )}

            {/* Crew - full list */}
            {view === 'crews' && (
              <Box
                height={LAYOUT_HEIGHT}
                sx={{ width: '100%', minWidth: 0, overflowX: 'auto', overflowY: 'auto' }}
              >
                {(allData.crews?.length ?? 0) === 0
                  ? (
                      <FlexCenter height="100%">
                        <Typography variant="h5" color="text.secondary">
                          No data available
                        </Typography>
                      </FlexCenter>
                  ) : (
                    <AnomalyEntityGrid rows={allData.crews} type="crew" />
                  )
                }
              </Box>
            )}

            <ScrollToTop scrollRef={scrollRef} />
          </Box>
        </Box>
      )}

      {trainOpen && (
        <TrainDialog
          open={trainOpen}
          onClose={() => setTrainOpen(false)}
          startDate={startDate}
          endDate={endDate}
        />
      )}
    </Box>
  );
};

export default AnomalyDetection;
